import { useEffect, useRef } from 'react';
import { Box, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { LuUser, LuHome, LuCopy, LuFileEdit } from 'react-icons/lu';
import { FormProvider, useForm } from 'react-hook-form';
import dayjs from 'dayjs';
import CustomDatePicker from '@Common/CustomDatePicker';
import type { IReservationDetail } from '../../types';

interface BookingDataTabProps {
  detail: IReservationDetail;
  formData: {
    checkIn: string;
    checkOut: string;
    checkInTime: string;
    checkOutTime: string;
    notes: string;
  };
  onFormChange: (data: {
    checkIn: string;
    checkOut: string;
    checkInTime: string;
    checkOutTime: string;
    notes: string;
  }) => void;
  isRtl: boolean;
}

interface IBookingForm {
  checkIn: string;
  checkOut: string;
  checkInTime: string;
  checkOutTime: string;
  notes: string;
}

const BookingDataTab = ({ detail, formData, onFormChange, isRtl }: BookingDataTabProps) => {
  const { t } = useTranslation();
  const isInitialSync = useRef(true);

  const methods = useForm<IBookingForm>({
    mode: 'onChange',
    defaultValues: {
      checkIn: formData.checkIn || '',
      checkOut: formData.checkOut || '',
      checkInTime: formData.checkInTime || '',
      checkOutTime: formData.checkOutTime || '',
      notes: formData.notes || '',
    },
  });

  // Sync parent formData → local form when detail loads (initial only)
  useEffect(() => {
    if (formData.checkIn && isInitialSync.current) {
      methods.reset({
        checkIn: formData.checkIn,
        checkOut: formData.checkOut,
        checkInTime: formData.checkInTime,
        checkOutTime: formData.checkOutTime,
        notes: formData.notes,
      });
      isInitialSync.current = false;
    } else if (!isInitialSync.current) {
      // After initial sync, update time fields if parent pushed a new value
      // (e.g. ManualReservation data loaded with correct times after initial detail sync)
      const current = methods.getValues();
      if (formData.checkInTime && formData.checkInTime !== current.checkInTime) {
        methods.setValue('checkInTime', formData.checkInTime, { shouldDirty: false });
      }
      if (formData.checkOutTime && formData.checkOutTime !== current.checkOutTime) {
        methods.setValue('checkOutTime', formData.checkOutTime, { shouldDirty: false });
      }
    }
  }, [formData, methods]);

  // Watch local form changes → propagate to parent
  useEffect(() => {
    const subscription = methods.watch((values) => {
      const checkIn = values.checkIn
        ? dayjs(values.checkIn as any).isValid()
          ? dayjs(values.checkIn as any).format('YYYY-MM-DD')
          : String(values.checkIn)
        : '';
      const checkOut = values.checkOut
        ? dayjs(values.checkOut as any).isValid()
          ? dayjs(values.checkOut as any).format('YYYY-MM-DD')
          : String(values.checkOut)
        : '';

      onFormChange({
        checkIn,
        checkOut,
        checkInTime: (values.checkInTime as string) || '',
        checkOutTime: (values.checkOutTime as string) || '',
        notes: (values.notes as string) || '',
      });
    });
    return () => subscription.unsubscribe();
  }, [methods, onFormChange]);

  const handleCopyReservationId = () => {
    if (detail.reservationId) {
      navigator.clipboard.writeText(detail.reservationId);
    }
  };

  return (
    <FormProvider {...methods}>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: '42px' }}>
        {/* Guest & Property info row */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          <Box sx={{ display: 'flex', gap: '24px' }}>
            {/* Guest info — first in DOM, appears on right in RTL */}
            <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '16px', alignItems: 'start' }}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px', alignItems: 'center' }}>
                <LuUser size={20} color="#8E98A1" />
                <Typography
                  sx={{
                    fontFamily: 'Tajawal, sans-serif',
                    fontSize: '12px',
                    color: '#8E98A1',
                  }}
                >
                  {t('TaskManagemnt.guest')}
                </Typography>
              </Box>
              <Typography
                sx={{
                  fontFamily: 'Tajawal, sans-serif',
                  fontSize: '14px',
                  color: '#0F172A',
                }}
              >
                {detail.guestName || '—'}
              </Typography>
              <Typography
                sx={{
                  fontFamily: 'Tajawal, sans-serif',
                  fontSize: '14px',
                  color: '#0F172A',
                  direction: 'ltr',
                }}
              >
                {detail.guestPhoneNumber || '—'}
              </Typography>
            </Box>

            {/* Property info — second in DOM, appears on left in RTL */}
            <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '16px', alignItems: 'start' }}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px', alignItems: 'center' }}>
                <LuHome size={20} color="#8E98A1" />
                <Typography
                  sx={{
                    fontFamily: 'Tajawal, sans-serif',
                    fontSize: '12px',
                    color: '#8E98A1',
                  }}
                >
                  {t('TaskManagemnt.property')}
                </Typography>
              </Box>
              <Typography
                sx={{
                  fontFamily: 'Tajawal, sans-serif',
                  fontSize: '14px',
                  color: '#0F172A',
                }}
              >
                {detail.propertyName}
              </Typography>
              <Box
                sx={{
                  display: 'flex',
                  gap: '8px',
                  alignItems: 'center',
                }}
              >
                <Box
                  onClick={handleCopyReservationId}
                  sx={{ cursor: 'pointer', display: 'flex', alignItems: 'center' }}
                >
                  <LuCopy size={16} color="#5B3FFF" />
                </Box>
                <Typography
                  sx={{
                    fontFamily: 'Tajawal, sans-serif',
                    fontSize: '14px',
                    color: '#5B3FFF',
                    lineHeight: '16.8px',
                  }}
                >
                  {detail.reservationId ? detail.reservationId.substring(0, 18) : '—'}
                </Typography>
                <Typography
                  sx={{
                    fontFamily: 'Tajawal, sans-serif',
                    fontSize: '14px',
                    color: '#0F172A',
                  }}
                >
                  {t('TaskManagemnt.reservationNumber')}
                </Typography>
              </Box>
            </Box>
          </Box>

          {/* Check-in date & time */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            <Box sx={{ display: 'flex', gap: '8px', alignItems: 'flex-start' }}>
              {/* Check-in date */}
              <Box sx={{ flex: 1 }}>
                <CustomDatePicker
                  views={['day']}
                  name="checkIn"
                  label={t('TaskManagemnt.checkInDate')}
                  format="YYYY-MM-DD"
                  withOutErrorMessage
                  readOnly
                />
              </Box>
              {/* Check-in time */}
              <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                <Typography
                  sx={{
                    fontFamily: 'Tajawal, sans-serif',
                    fontWeight: 700,
                    fontSize: '15px',
                    color: '#435363',
                    mb: '4px',
                  }}
                >
                  {t('TaskManagemnt.checkInTime')}
                </Typography>
                <Box
                  sx={{
                    width: '100%',
                    height: '49px',
                    display: 'flex',
                    alignItems: 'center',
                    px: '11px',
                    border: '1px solid #E5E7EB',
                    borderRadius: '12px',
                    backgroundColor: '#fff',
                  }}
                >
                  <Typography sx={{ fontFamily: 'Tajawal, sans-serif', fontWeight: 500, fontSize: '15px', color: '#261b6a' }}>
                    {methods.watch('checkInTime') || '—'}
                  </Typography>
                </Box>
              </Box>


            </Box>

            {/* Check-out date & time */}
            <Box sx={{ display: 'flex', gap: '8px', alignItems: 'flex-start' }}>
              {/* Check-out date */}
              <Box sx={{ flex: 1 }}>
                <CustomDatePicker
                  views={['day']}
                  name="checkOut"
                  label={t('TaskManagemnt.checkOutDate')}
                  format="YYYY-MM-DD"
                  withOutErrorMessage
                  readOnly
                />
              </Box>
              {/* Check-out time */}
              <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                <Typography
                  sx={{
                    fontFamily: 'Tajawal, sans-serif',
                    fontWeight: 700,
                    fontSize: '15px',
                    color: '#435363',
                    mb: '4px',
                  }}
                >
                  {t('TaskManagemnt.checkOutTime')}
                </Typography>
                <Box
                  sx={{
                    width: '100%',
                    height: '49px',
                    display: 'flex',
                    alignItems: 'center',
                    px: '11px',
                    border: '1px solid #E5E7EB',
                    borderRadius: '12px',
                    backgroundColor: '#fff',
                  }}
                >
                  <Typography sx={{ fontFamily: 'Tajawal, sans-serif', fontWeight: 500, fontSize: '15px', color: '#261b6a' }}>
                    {methods.watch('checkOutTime') || '—'}
                  </Typography>
                </Box>
              </Box>


            </Box>
          </Box>
        </Box>

        {/* Notes section */}
        <Box
          sx={{
            display: 'flex',
            gap: '12px',
            alignItems: 'flex-start',
            border: '1px solid #D9D9E9',
            borderRadius: '8px',
            p: '14px',
          }}
        >
          <LuFileEdit size={12} color="#5B3FFF" style={{ flexShrink: 0, marginTop: '4px' }} />
          <textarea
            value={methods.watch('notes')}
            onChange={(e) => methods.setValue('notes', e.target.value)}
            rows={2}
            style={{
              border: 'none',
              outline: 'none',
              fontFamily: 'Tajawal, sans-serif',
              fontSize: '14px',
              color: methods.watch('notes') ? '#0F172A' : '#5B3FFF',
              width: '100%',
              resize: 'none',
              backgroundColor: 'transparent',
            }}
            placeholder={t('TaskManagemnt.notePlaceholder')}
          />
        </Box>
      </Box>
    </FormProvider>
  );
};

export default BookingDataTab;
